import {
	AfterViewInit,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Optional,
	Output,
} from "@angular/core";
import { FormGroupDirective } from "@angular/forms";
import {
	addressRestrictionKeysOnly,
	AddressSearchResult,
	AddressService,
	PRODUCT_NAME,
} from "app/shared";
import { debounceTime, map, take, takeUntil } from "rxjs/operators";
import { FormFieldDirective } from "../form-field";
import { FormFieldType } from "../form-field.model";

@Component({
    selector: "tc-address-field",
    templateUrl: "./address-field.component.html",
    styleUrls: ["./address-field.component.scss"],
    standalone: false
})
export class AddressFieldComponent
	extends FormFieldDirective
	implements OnInit, AfterViewInit {
	_addressRestrictionKeysOnly = addressRestrictionKeysOnly;
	activeOptionIndex = -1;
	addresses: AddressSearchResult[] = [];
	busy = false;
	isEnergy: boolean;
	addressValSelected = false;
	@Input() placeholderImageUrl: string;
	@Input() placeholderImageSize = 14;
	@Input() queryThreshold = 3;
	@Input() showManualEntryOption = true;
	@Input() showHintError = false;
	@Input() currentProduct: any;
	@Input() scroll: boolean;
	@Input() defaultSelected: any;
	@Output() optionSelected = new EventEmitter<AddressSearchResult>();

	constructor(
		private addressService: AddressService,
		@Optional()
		@Inject(FormGroupDirective)
		formGroupDirective: FormGroupDirective
	) {
		super(formGroupDirective);
	}

	ngOnInit(): void {
		this.type = FormFieldType.ADDRESS;
		this.isEnergy = this.currentProduct === PRODUCT_NAME.energy;
		this.control.valueChanges
			.pipe(debounceTime(250), takeUntil(this.unsubscribeAll))
			.subscribe((val) => {
				if (!this.addressValSelected) {
					val?.trim().length >= this.queryThreshold
						? this.updateAddressSuggestions(val)
						: (this.addresses = []);
				}
			});
	}

	ngAfterViewInit(): void {
		if (this.defaultSelected) {
			setTimeout(() => {
				if (this.defaultSelected.line1) {
					const { postcode, state, gnafId, line1, suburb } =
						this.defaultSelected;
					const mappedAdd = {
						composite: `${line1} ${suburb} ${state} ${postcode}`,
						gnafId,
						line1,
						postcode,
						country: "Australia",
						state,
						suburb,
					};
					this.addressSelected(mappedAdd as any);
				} else {
					const { postCode, stateCode, gnafId, street, city } =
						this.defaultSelected;
					const mappedAdd = {
						composite: `${street} ${city} ${stateCode} ${postCode}`,
						gnafId,
						line1: street,
						postcode: postCode,
						country: "Australia",
						state: stateCode,
						suburb: city,
					};
					this.addressSelected(mappedAdd as any);
				}
			}, 50);
		} else if (!this.defaultSelected && this.control.value) {
			// No defaultSelected (mobileAddressDefaultSelectedValue) from store, but has default value from form initial value
			this.addressValSelected = true;
		}
	}

	get validationImage(): string {
		if (this.control.dirty && !this.control.errors && !!this.control.value && this.addressValSelected) {
			return "/assets/img/icons/icon-tick-green.svg";
		}
		if (this.hasError) {
			return "/assets/img/icons/exclamation-large.svg";
		}
		return "";
	}

	addressSelected(selectedOption: AddressSearchResult): void {
		this.activeOptionIndex = -1;
		this.addresses = [];
		this.addressValSelected = true;
		this.control.setValue(selectedOption?.composite || "", {
			emitEvent: false,
		});
		this.optionSelected.emit(selectedOption);
	}

	onKeydown(event: KeyboardEvent): void {
		this.addressValSelected = false;
		if (event.key === "ArrowUp") {
			event.preventDefault();
			if (this.activeOptionIndex > 0) {
				this.activeOptionIndex--;
			} else {
				this.activeOptionIndex = this.addresses.length;
			}
		} else if (event.key === "ArrowDown") {
			event.preventDefault();
			if (this.activeOptionIndex < this.addresses.length) {
				this.activeOptionIndex++;
			} else {
				this.activeOptionIndex = 0;
			}
		} else if (event.key === "Enter" && this.activeOptionIndex >= 0) {
			event.preventDefault();
			const add =
				this.activeOptionIndex <= this.addresses.length - 1
					? this.addresses[this.activeOptionIndex]
					: null;
			this.addressSelected(add);
		}
	}

	get showAddressList(): boolean {
		return (
			this.control.value?.length > 2 &&
			!this.busy &&
			!this.addressValSelected
		);
	}

	getAddress(address): string {
		return this.isEnergy ? address?.Address : address?.composite;
	}

	private updateAddressSuggestions(val: string): void {
		this.busy = true;
		this.addressService
			.suggestion(!this.isEnergy, val, this.isEnergy)
			.pipe(
				map((result: any) => {
					return this.isEnergy
						? result.splice(0, 10)
						: result?.compositeAddress?.splice(0, 10);
				}),
				take(1)
			)
			.subscribe(
				(addresses) => {
					this.addresses = addresses;
					this.busy = false;
				},
				(err) => {
					this.busy = false;
					console.error(err);
				}
			);
	}
}
